/**
 * 统一服务初始化插件
 *
 * 使用 common 的 AppInitService 统一管理所有服务
 * 整合了原 00.boot.ts 的功能（dayjs、安全域名检测、时区、useHead 等）
 */

import { Intersect } from '@/directives/intersect';
import { getDefaultLocale, isChinese } from '@/utils/area';
import { getCookie, setCookie } from '@/utils/cookie';
import { localGetItem } from '@/utils/localStorage';
import type { CustomAxiosResponse } from '@rendora/common/entities';
import EN from '@rendora/common/locales/en-US.json';
import CHS from '@rendora/common/locales/zh-CN.json';
import { initializeApp, type AppConfig } from '@rendora/common/services';
import { getApiClient } from '@rendora/common/services/api';
import {
  createAuthRequestInterceptor,
  createHeadersRequestInterceptor,
  createHttpErrorInterceptor,
} from '@rendora/common/services/api/interceptors';
import { getBuriedService } from '@rendora/common/services/buried';
import { getI18nService } from '@rendora/common/services/i18n';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { usePermissionData } from '~/composables/permission';
import { useLoginOut } from '~/composables/user';
import { COOKIE_KEY } from '~/constants/config';
import { PageKey } from '~/constants/page';
import { SAFE_HOST } from '~/utils/constant';

// 初始化 dayjs 插件
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

export default defineNuxtPlugin(async ({ vueApp, nuxtApp }) => {
  const requestURL = useRequestURL();
  const runtimeConfig = useRuntimeConfig();
  const route = useRoute();

  // 0. window 上挂载 ENV_CONFIG
  if (import.meta.client) {
    window.ENV_CONFIG = {
      AREA: runtimeConfig.public.area as 'cn' | 'us',
      API_BASE: runtimeConfig.public.apiBase,
      FE_OSS: runtimeConfig.public.feOss,
      BE_OSS: runtimeConfig.public.beOss,
      WS_MESSAGE_CENTER: runtimeConfig.public.wsMessageCenter,
      API_MESSAGE_CENTER: runtimeConfig.public.apiMessageCenter,
      API_DOMAIN: runtimeConfig.public.apiDomain,
      ENV: runtimeConfig.public.env,
      WORKSPACE_DOMAIN: runtimeConfig.public.workspaceUrl,
    };
  }

  // 1. 初始化安全域名检测
  if (
    !runtimeConfig.public.privatization &&
    runtimeConfig.public.env === 'production' &&
    process.env.NODE_ENV === 'production' &&
    !SAFE_HOST.some(item => item.test(requestURL.host)) &&
    route.name !== PageKey.NOT_FOUND
  ) {
    navigateTo('/not-found');
    return {};
  }

  // 2. 初始化时区信息
  // 注意：dayjs.tz.setDefault 是全局设置，在 SSR 中可能影响其他请求
  // 但这是必要的，因为每个请求的时区可能不同
  const timezoneValue = useCookie(COOKIE_KEY.TIMEZONE).value || 'Asia/Shanghai';
  dayjs.tz.setDefault(timezoneValue);
  if (import.meta.client) {
    setCookie(COOKIE_KEY.TIMEZONE, dayjs.tz.guess());
  }

  // 3. 配置 useHead（HTML 属性和脚本）
  useHead({
    htmlAttrs: {
      lang: getDefaultLocale(),
    },
    script: [
      {
        src: `${runtimeConfig.app.cdnURL}/lib-pag/libpag.min.js`,
        defer: true,
      },
    ],
  });

  // 构建 API 请求拦截器
  const isPrivatization = runtimeConfig.public.privatization;
  const requestInterceptors = [
    createAuthRequestInterceptor({
      getToken: () => {
        const session = import.meta.client
          ? localGetItem(COOKIE_KEY.USER_TOKEN)
          : getCookie(COOKIE_KEY.USER_TOKEN);
        if (session?.user_id) {
          return { userId: session.user_id, key: session.key };
        }
        return null;
      },
    }),
    createHeadersRequestInterceptor({
      getHeaders: () => {
        const headers: Record<string, string | undefined> = {
          'Accept-Language': isChinese() ? 'zh-CN' : 'en',
        };
        if (!isPrivatization) {
          const randomId = import.meta.client
            ? localGetItem(COOKIE_KEY.USER_RANDOM_ID)
            : getCookie(COOKIE_KEY.USER_RANDOM_ID);
          if (randomId) {
            headers['Session-User'] = randomId;
          }
        }
        return headers;
      },
    }),
  ];

  // 构建 API 响应拦截器
  const responseInterceptors = [
    async (response: CustomAxiosResponse): Promise<CustomAxiosResponse> => {
      return response;
    },
  ];

  // 构建错误拦截器
  const errorInterceptors = createHttpErrorInterceptor({
    clientOnly: true,
    on401: async () => {
      if (!import.meta.client) return;
      const { message } = await import('@rendora/common/composables/message');
      message.clear();
      const permissionData = usePermissionData();
      await useLoginOut();
      const link = permissionData.value?.config?.logout_url
        ? permissionData.value.config.logout_url
        : `${location.origin}/login?redirect=${encodeURIComponent(encodeURIComponent(location.href))}`;
      location.href = link!;
    },
    showError: async msg => {
      const { message } = await import('@rendora/common/composables/message');
      message.error(msg);
    },
    defaultErrorMessage: '系统异常',
  });

  // 构建应用配置
  const appConfig: AppConfig = {
    // API 配置（必需）
    api: {
      baseURL: runtimeConfig.public.apiBase,
      timeout: 30000,
      autoErrorHandle: true,
      requestInterceptors,
      responseInterceptors,
      errorInterceptors,
    },

    // Store 配置
    store: {},

    // I18n 配置
    i18n: {
      legacy: false,
      globalInjection: true,
      defaultLocale: getDefaultLocale(),
      messages: {
        'zh-CN': CHS,
        'en-US': EN,
      },
    },

    // 埋点配置（仅在客户端且非私有化环境）
    buried:
      import.meta.client && !runtimeConfig.public.privatization
        ? {
            autoTrack: true,
            enableClick: true,
            enableExposure: true,
            exposureThreshold: 0.5,
            customEvents: {},
            enableDevLog: runtimeConfig.public.env !== 'production',
          }
        : undefined,

    // OSS 配置（如果需要可以启用）
    oss:
      import.meta.client && !runtimeConfig.public.privatization
        ? {
            enableAutoCleanup: true,
            cleanupInterval: 30000,
            maxConcurrentUploads: 3,
          }
        : undefined,

    // SSE 配置（如果需要可以启用）
    sse:
      import.meta.client && !runtimeConfig.public.privatization
        ? {
            baseURL: runtimeConfig.public.apiMessageCenter,
            autoConnect: false,
            // 使用函数工厂，每次调用时重新获取 session，避免闭包持有长期引用
            getAuthInfo: () => {
              // 每次调用时重新获取，避免闭包持有对 composable 的引用
              const session = useSession();
              if (session.value?.user_id) {
                return {
                  userId: session.value.user_id,
                  key: session.value.key,
                };
              }
              return null;
            },
          }
        : undefined,

    // WebSocket 配置（如果需要可以启用）
    websocket:
      import.meta.client && !runtimeConfig.public.privatization
        ? {
            baseURL: runtimeConfig.public.wsMessageCenter,
            autoConnect: false,
            // 使用函数工厂，每次调用时重新获取 session，避免闭包持有长期引用
            getAuthInfo: () => {
              // 每次调用时重新获取，避免闭包持有对 composable 的引用
              const session = useSession();
              if (session.value?.user_id) {
                return {
                  userId: session.value.user_id,
                  key: session.value.key,
                };
              }
              return null;
            },
          }
        : undefined,
  };

  try {
    // 初始化所有服务
    const appInitService = await initializeApp(vueApp, appConfig);

    // SSR 环境下将服务实例存储到 nuxtApp 上，以便在请求结束时清理
    if (import.meta.server && nuxtApp) {
      // @ts-expect-error - 动态添加属性
      nuxtApp.$appInitService = appInitService;
    }

    // 注册全局指令（整合自 03.enhance.ts）
    vueApp.directive('intersect', Intersect);

    // 获取服务实例（用于 provide）
    const i18nService = getI18nService();

    // 提供服务实例给应用使用
    return {
      provide: {
        // 应用初始化服务实例
        appInitService,

        // 便捷的服务访问方法
        getApiClient: async () => await getApiClient(),
        getBuriedService: () => getBuriedService(),
        getI18nService: () => i18nService,

        // 保持向后兼容的接口
        t: i18nService.getI18n().global.t,

        // dayjs 实例（从原 00.boot.ts 迁移）
        dayjs,
      },
    };
  } catch (error) {
    console.error('[Services Plugin] 服务初始化失败:', error);
    // 返回空对象，避免应用崩溃
    return {};
  }
});
